import api from '@/utils/api';
import { getSigV4HeadersForGet, transitionExpiredToken } from '@/utils/cognito';
import { insertServiceIdInApiUrlSync } from '@/utils/common';
import { ApiUrl } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import useSetLoading from '@/hooks/useSetLoading';
import { JudgeAccountTransferResponse } from '@/types/api/mypage/services/payment/restriction/judgeAccountTransfer';

/**
 * 「支払情報：規制情報（口座振替）」の処理を実施する
 */
const useApiJudgeAccountTransfer = () => {
  const { startLoading, finishLoading } = useSetLoading();
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();

  /**
   * 規制情報（口座振替）取得を実施する
   * @param serviceId サービスID
   * @returns 取得結果
   */
  const get = async ({
    serviceId,
  }: {
    serviceId: string;
  }): Promise<AxiosResponse<JudgeAccountTransferResponse>> => {
    // 支払規制情報は契約サービス単位で異なるため、明示 serviceId でURLを組み立てる。
    const url = insertServiceIdInApiUrlSync(
      ApiUrl.Read.Services.Payment.RESTRICTION_JUDGEACCOUNTTRANSFER,
      serviceId
    );
    try {
      startLoading(url);
      const headers = await getSigV4HeadersForGet(url);
      const response = await api.get(url, headers);
      const { isError } = commonResponseCodeHandling({
        responseCode: response.data.response_code,
      });
      if (isError) {
        // 業務エラー時は後続の支払変更処理へ進ませないため例外化する。
        throw new Error('データの取得に失敗しました');
      }
      return response;
    } catch (error: any) {
      if (!transitionExpiredToken(error)) {
        // トークン切れ以外は共通エラーハンドリングへ寄せる。
        commonErrorHandling({ error });
      }
      throw error;
    } finally {
      finishLoading(url);
    }
  };
  return { get };
};

export default useApiJudgeAccountTransfer;
